import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../core/providers/AuthProvider';
import ListNotifications from '../../../use-cases/notifications/ListNotifications';
import MarkNotificationAsRead from '../../../use-cases/notifications/MarkNotificationAsRead';
import MarkAllNotificationsAsRead from '../../../use-cases/notifications/MarkAllNotificationsAsRead';
import { repositories } from '../../../infrastructure/config/repositories';
import { onSnapshot, query, collection, where } from 'firebase/firestore';
import { db } from '../../../firebase';

// Créer un event bus simple pour les notifications
export const notificationEvents = {
  onUnreadCountChange: new Set(),
  emit(count) {
    console.log('Emitting notification count:', count);
    this.onUnreadCountChange.forEach(callback => callback(count));
  }
};

export const useNotifications = () => {
  const { user } = useAuth();
  const [state, setState] = useState({
    notifications: [],
    loading: false,
    error: null,
    hasMore: true,
    lastVisible: null
  });
  
  const notificationsRef = useRef([]);
  const initialLoadDoneRef = useRef(false);
  const loadNotificationsRef = useRef(null);

  // Mettre à jour le compteur de notifications non lues
  const updateUnreadCount = useCallback(() => {
    const unreadCount = state.notifications.filter(n => !n.read).length;
    notificationEvents.emit(unreadCount);
  }, [state.notifications]);

  // Appeler updateUnreadCount quand les notifications changent
  useEffect(() => {
    updateUnreadCount();
  }, [state.notifications, updateUnreadCount]);

  const loadNotifications = useCallback(async (isInitial = false) => {
    if (!user?.uid) return;

    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const listNotifications = new ListNotifications(repositories.notifications);
      const result = await listNotifications.execute(user.uid, {
        lastVisible: isInitial ? null : state.lastVisible
      });

      setState(prev => ({
        ...prev,
        notifications: isInitial ? result.notifications : [...prev.notifications, ...result.notifications],
        lastVisible: result.lastVisible,
        hasMore: result.hasMore,
        loading: false
      }));
    } catch (err) {
      console.error('Error loading notifications:', err);
      setState(prev => ({
        ...prev,
        error: "Impossible de charger les notifications pour le moment",
        loading: false
      }));
    }
  }, [user?.uid, state.lastVisible]);

  // Garder une référence à jour de loadNotifications
  loadNotificationsRef.current = loadNotifications;

  // Effet pour le chargement initial uniquement
  useEffect(() => {
    if (user?.uid && !initialLoadDoneRef.current) {
      initialLoadDoneRef.current = true;
      loadNotificationsRef.current(true);
    }
  }, [user?.uid]);

  useEffect(() => {
    notificationsRef.current = state.notifications;
  }, [state.notifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const markAsReadUseCase = new MarkNotificationAsRead(repositories.notifications);
      await markAsReadUseCase.execute(notificationId);
      
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user?.uid) return;

    try {
      const markAllAsReadUseCase = new MarkAllNotificationsAsRead(repositories.notifications);
      await markAllAsReadUseCase.execute(user.uid);
      
      setState(prev => ({
        ...prev,
        notifications: prev.notifications.map(notif => ({ ...notif, read: true }))
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [user?.uid]);

  useEffect(() => {
    return () => {
      const unreadNotifications = notificationsRef.current.filter(notif => !notif.read);
      if (unreadNotifications.length > 0 && user?.uid) {
        const markAllAsReadUseCase = new MarkAllNotificationsAsRead(repositories.notifications);
        markAllAsReadUseCase.execute(user.uid).catch(console.error);
      }
    };
  }, [user?.uid]);

  return useMemo(() => ({
    notifications: state.notifications,
    loading: state.loading,
    error: state.error,
    hasMore: state.hasMore,
    loadMore: () => loadNotificationsRef.current(false),
    markAsRead,
    markAllAsRead
  }), [state, markAsRead, markAllAsRead]);
};

// Hook pour utiliser le compteur de notifications
export const useUnreadNotificationsCount = () => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    console.log('Setting up unread count listener');
    const handleCountChange = (newCount) => {
      console.log('Received new count:', newCount);
      setCount(newCount);
    };
    
    notificationEvents.onUnreadCountChange.add(handleCountChange);
    
    return () => {
      console.log('Cleaning up unread count listener');
      notificationEvents.onUnreadCountChange.delete(handleCountChange);
    };
  }, []);

  return count;
};

// Nouveau hook pour le compteur global
export const useInitializeNotificationsCount = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.uid) {
      console.log('No user, skipping notification count init');
      return;
    }

    console.log('Setting up notifications listener');
    
    // Créer la query pour les notifications non lues
    const q = query(
      collection(db, 'notifications'),
      where('recipientId', '==', user.uid),
      where('read', '==', false)
    );

    // Écouter les changements en temps réel
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const count = snapshot.size;
      console.log('Real-time notification count update:', count);
      notificationEvents.emit(count);
    }, (error) => {
      console.error('Error in notifications listener:', error);
    });

    // Nettoyer l'écouteur quand le composant est démonté
    return () => {
      console.log('Cleaning up notifications listener');
      unsubscribe();
    };
  }, [user?.uid]);
}; 